import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../utils/apiUtils';

/**
 * Fetches the unread notification count.
 *
 * Why this is a React Query hook (instead of useEffect + apiRequest in each
 * consumer): without a shared query key, two components both fetching this
 * count race-fetch in parallel on first paint. React StrictMode in dev mode
 * also double-fires raw useEffects. With this hook every consumer sharing
 * `['unread-notification-count', userId]` is deduped by React Query —
 * exactly one in-flight request per cache window regardless of how many
 * components subscribe.
 */
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes — count is not time-critical
const STALE_TIME_MS = 60 * 1000;        // 1 minute — fast feedback after a notification action

// Exported so callers (mark-as-read flows, mark-all-as-read, etc.) can
// invalidate the cache after a mutation that changes the unread count.
// Without this, optimistic updates pushed into UIContext can be silently
// overwritten by stale cached values on the next Header re-render.
export const UNREAD_COUNT_QUERY_KEY = 'unread-notification-count';

/**
 * Hook for callers that mutate notifications and want the unread count to
 * refetch on the next render. Use after mark-as-read / mark-all-as-read /
 * delete-notification etc.
 */
export const useInvalidateUnreadNotificationCount = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [UNREAD_COUNT_QUERY_KEY] });
};

export const useUnreadNotificationCount = (userId, options = {}) => {
  const { enabled = !!userId, ...queryOptions } = options;

  return useQuery({
    queryKey: [UNREAD_COUNT_QUERY_KEY, userId],
    queryFn: async ({ signal }) => {
      const response = await apiRequest(
        '/notifications?limit=1&unread_only=true',
        { signal }
      );
      // The endpoint returns { success, unread_count, ... }; surface the
      // count and a sane fallback.
      return response?.success ? (response.unread_count ?? 0) : 0;
    },
    enabled,
    staleTime: STALE_TIME_MS,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    // Don't use `placeholderData: 0` — consumers that mirror this into
    // UIContext would briefly hide the badge if there are unread
    // notifications. Let `data` stay undefined while loading; consumers
    // should sync only when typeof data === 'number'.
    ...queryOptions,
  });
};
